import React, { useEffect, useMemo, useRef, useState } from "react";
import { Animated, Dimensions, ScrollView } from "react-native";

import type { SimpleRecipe } from "../services/backendTypes";

interface AnimatedRecipeCarouselProps {
  data: SimpleRecipe[];
  renderItem: (item: SimpleRecipe, scale: Animated.AnimatedInterpolation<number>) => JSX.Element;
  onActiveIndexChange: (index: number) => void;
}

const SCREEN_WIDTH = Dimensions.get("window").width;
const CARD_WIDTH = 172; // Base card width in logical pixels (matches Figma)
const CARD_MARGIN = 32; // Increased margin to account for scaling (prevent overlap)
const CARD_WITH_MARGIN = CARD_WIDTH + CARD_MARGIN;

export const AnimatedRecipeCarousel: React.FC<AnimatedRecipeCarouselProps> = ({
  data,
  renderItem,
  onActiveIndexChange,
}) => {
  const scrollViewRef = useRef<ScrollView>(null);
  const scrollX = useRef(new Animated.Value(0)).current;
  const [isScrolling, setIsScrolling] = useState(false);

  // Create infinite data by duplicating items
  const infiniteData = useMemo(() => {
    if (data.length === 0) return [];
    if (data.length < 3) {
      // For small datasets, duplicate more times to ensure smooth infinite scroll
      return [...data, ...data, ...data, ...data, ...data];
    }
    const lastThree = data.slice(-3);
    const firstThree = data.slice(0, 3);
    return [...lastThree, ...data, ...firstThree];
  }, [data]);

  // Calculate initial scroll position to show first real item centered
  const initialScrollX = useMemo(() => {
    if (data.length === 0) return 0;
    if (data.length < 3) {
      return 0; // Start at beginning for small datasets
    }
    // Position so the first real card (at index 3 in infinite data) is centered
    return 3 * CARD_WITH_MARGIN;
  }, [data.length]);

  // Set initial scroll position and active index
  useEffect(() => {
    if (infiniteData.length > 0) {
      // Set initial active index to 0 (first real item)
      onActiveIndexChange(0);

      setTimeout(() => {
        scrollViewRef.current?.scrollTo({
          x: initialScrollX,
          animated: false,
        });
      }, 100);
    }
  }, [initialScrollX, infiniteData.length, onActiveIndexChange]);

  // Calculate scale for each card based on its distance from center
  const getCardScale = (index: number): Animated.AnimatedInterpolation<number> => {
    const inputRange = [
      (index - 2) * CARD_WITH_MARGIN,
      (index - 1) * CARD_WITH_MARGIN,
      index * CARD_WITH_MARGIN,
      (index + 1) * CARD_WITH_MARGIN,
      (index + 2) * CARD_WITH_MARGIN,
    ];

    return scrollX.interpolate({
      inputRange,
      outputRange: [1, 1, 1.5, 1, 1], // 1.5x scale when centered (matches Figma: 256/172 ≈ 1.49)
      extrapolate: "clamp",
    });
  };

  // Handle scroll events for infinite scrolling
  const handleScroll = (event: { nativeEvent: { contentOffset: { x: number } } }): void => {
    const offsetX = event.nativeEvent.contentOffset.x;
    const dataLength = data.length;

    if (dataLength < 3) {
      // Simple pagination for small datasets
      const index = Math.round(offsetX / CARD_WITH_MARGIN) % dataLength;
      onActiveIndexChange(Math.max(0, index));
      return;
    }

    // Handle infinite scroll repositioning
    if (!isScrolling) {
      // If scrolled to duplicated items at start
      if (offsetX <= CARD_WITH_MARGIN * 2) {
        scrollViewRef.current?.scrollTo({
          x: offsetX + dataLength * CARD_WITH_MARGIN,
          animated: false,
        });
        return;
      }

      // If scrolled to duplicated items at end
      if (offsetX >= CARD_WITH_MARGIN * (dataLength + 3)) {
        scrollViewRef.current?.scrollTo({
          x: offsetX - dataLength * CARD_WITH_MARGIN,
          animated: false,
        });
        return;
      }
    }

    // Calculate actual index for pagination dots
    const rawIndex = Math.round(offsetX / CARD_WITH_MARGIN);
    const actualIndex = (((rawIndex - 3) % dataLength) + dataLength) % dataLength;
    onActiveIndexChange(actualIndex);
  };

  const handleScrollBeginDrag = (): void => {
    setIsScrolling(true);
  };

  const handleScrollEndDrag = (): void => {
    setIsScrolling(false);
  };

  return (
    <Animated.ScrollView
      ref={scrollViewRef}
      horizontal
      showsHorizontalScrollIndicator={false}
      scrollEventThrottle={16}
      decelerationRate="fast"
      snapToInterval={CARD_WITH_MARGIN}
      snapToAlignment="start"
      style={{ flex: 1 }} // Take full height of parent
      contentContainerStyle={{
        paddingLeft: (SCREEN_WIDTH - CARD_WIDTH) / 2 - 18, // Balanced compensation for active card's left margin
        paddingRight: (SCREEN_WIDTH - CARD_WIDTH) / 2, // Keep original right padding
        alignItems: "center", // Center content vertically
      }}
      onScroll={Animated.event([{ nativeEvent: { contentOffset: { x: scrollX } } }], {
        useNativeDriver: false, // Can't use native driver with ScrollView content changes
        listener: handleScroll,
      })}
      onScrollBeginDrag={handleScrollBeginDrag}
      onScrollEndDrag={handleScrollEndDrag}
    >
      {infiniteData.map((item, index) => {
        const scale = getCardScale(index);

        // Keep original marginRight system, but add equal marginLeft for scaled cards
        const animatedMarginRight = scale.interpolate({
          inputRange: [1, 1.5],
          outputRange: [CARD_MARGIN, CARD_MARGIN + 25], // 32px -> 57px (reduced to balance with left)
          extrapolate: "clamp",
        });

        const animatedMarginLeft = scale.interpolate({
          inputRange: [1, 1.5],
          outputRange: [0, 25], // 0px -> 25px (equal to right margin increase)
          extrapolate: "clamp",
        });

        return (
          <Animated.View
            key={`${item.id}-${index}`}
            style={{
              width: CARD_WIDTH,
              marginRight: animatedMarginRight,
              marginLeft: animatedMarginLeft,
              transform: [{ scale }],
              justifyContent: "center", // Center the card content
            }}
          >
            {renderItem(item, scale)}
          </Animated.View>
        );
      })}
    </Animated.ScrollView>
  );
};

export default AnimatedRecipeCarousel;
